import cx from 'classnames'
import { memo, ReactNode, useMemo } from 'react'
import DPFPremiumFeatures1 from '../assets/landing/DPF-premium-features-1.png'
import DPFPremiumFeatures2 from '../assets/landing/DPF-premium-features-2.png'
import DPFPremiumFeatures3 from '../assets/landing/DPF-premium-features-3.png'
import DPFStepsImg1 from '../assets/landing/DPF-steps-img-1.png'
import DPFStepsImg2 from '../assets/landing/DPF-steps-img-2.png'
import DPFStepsImg3 from '../assets/landing/DPF-steps-img-3.png'

interface StepButton {
  text: string
  variant: 'black' | 'red'
  href: string
  onClick?: () => void
}

interface PremiumFeature {
  icon: string
  text: string
}

interface Step {
  stepNumber: number
  badgeText: string
  imageSrc: string
  title: ReactNode
  description: string
  buttons?: StepButton[]
  premiumFeatures?: PremiumFeature[]
  onClick?: () => void
}

const StepBadge = memo(function StepBadge({ text }: { text: string }) {
  return (
    <div className='flex justify-center'>
      <div className='bg-[#F29CB2] text-white px-2 py-0.5 rounded-full text-[13px] font-semibold'>
        {text}
      </div>
    </div>
  )
})

const StepButtons = memo(function StepButtons({ buttons }: { buttons: StepButton[] }) {
  return (
    <div className='flex flex-col gap-3 w-full mx-auto'>
      {buttons.map(button => (
        <a
          id={button.text}
          key={button.text}
          className={cx('w-full py-3 font-semibold rounded-lg text-xl text-white text-center', {
            'bg-[#202020]': button.variant === 'black',
            'bg-[#C70000]': button.variant === 'red',
          })}
          href={button.href}
          target='_blank'
          rel='noreferrer noopener'
          onClick={button.onClick ? button.onClick : undefined}
        >
          {button.text}
        </a>
      ))}
    </div>
  )
})

const PremiumFeaturesBox = memo(function PremiumFeaturesBox({
  features,
}: {
  features: PremiumFeature[]
}) {
  return (
    <div className='rounded-lg overflow-hidden mt-6 mx-2'>
      <div className='bg-[#60B3D7] text-white px-4 py-2 font-semibold text-center'>
        プレミアム人気機能
      </div>
      <div className='p-4 pl-8 space-y-4 bg-[#F5F5F5]'>
        {features.map((feature, idx) => (
          <div key={idx} className='flex items-center gap-4'>
            <img
              src={feature.icon}
              alt=''
              className='w-16 h-16 flex-shrink-0 object-contain'
              loading='lazy'
            />
            <p className='text-gray-12 font-semibold text-base flex-1'>{feature.text}</p>
          </div>
        ))}
      </div>
    </div>
  )
})

export default function PurchaseStepsSection({
  logClickedPurchaseStepsButtonAmazon,
  logClickedPurchaseStepsButtonRakuten,
  isAmazonSaleActive,
}: {
  logClickedPurchaseStepsButtonAmazon: () => void
  logClickedPurchaseStepsButtonRakuten: () => void
  isAmazonSaleActive: boolean
}) {
  const steps = useMemo<Step[]>(
    () => [
      {
        stepNumber: 1,
        badgeText: 'STEP 1',
        imageSrc: DPFStepsImg1,
        title: '本体を購入',
        description: 'おもいでフォトフレームは以下の販売サイトからご購入いただけます。',
        buttons: [
          {
            text: 'Amazonで購入',
            variant: 'black',
            href: 'https://www.amazon.co.jp/dp/B0G117GHSW',
            onClick: () => logClickedPurchaseStepsButtonAmazon(),
          },
          {
            text: '楽天市場で購入',
            variant: 'red',
            href: 'https://item.rakuten.co.jp/mixiofficial/mdp01-j/',
            onClick: () => logClickedPurchaseStepsButtonRakuten(),
          },
        ],
      },
      {
        stepNumber: 2,
        badgeText: 'STEP 2',
        imageSrc: DPFStepsImg2,
        title: (
          <>
            本体が届いたらQRコードを
            <br />
            読み取るだけで、みてねと連携！
          </>
        ),
        description:
          '本体を電源につなぎ、Wi-Fi設定等をした後にQRコードをスマートフォンで読み込むだけで簡単にみてねと連携できます。',
      },
      {
        stepNumber: 3,
        badgeText: 'STEP 3: オプショナル',
        imageSrc: DPFStepsImg3,
        title: (
          <>
            プレミアム・Proプランに登録して
            <br />
            無制限に楽しめる！
          </>
        ),
        description:
          'フォトフレームが無制限に使えるだけでなく、みてねアプリで使える機能が増えます!',
        premiumFeatures: [
          {
            icon: DPFPremiumFeatures1,
            text: '年間版1秒動画がもらえる',
          },
          {
            icon: DPFPremiumFeatures2,
            text: '2分以上の動画アップロード',
          },
          {
            icon: DPFPremiumFeatures3,
            text: '広告なしで快適に使える',
          },
        ],
      },
    ],
    [logClickedPurchaseStepsButtonAmazon, logClickedPurchaseStepsButtonRakuten],
  )

  return (
    <section className='px-4 py-8 space-y-6 pb-10'>
      <div className='text-center'>
        <h2 className='text-2xl font-semibold text-gray-12'>
          購入からご利用まで
          <br />
          かんたんステップ
        </h2>
      </div>
      <div className='space-y-10'>
        {steps.map(step => (
          <div key={step.stepNumber}>
            <img
              src={step.imageSrc}
              alt={step.badgeText}
              className='w-full rounded-lg mb-6'
              loading='lazy'
            />
            <StepBadge text={step.badgeText} />
            <div className='space-y-4 mt-2'>
              <h3 className='text-xl font-semibold text-gray-12 text-center leading-[140%] tracking-0'>
                {step.title}
              </h3>
              <p className='text-base font-normal text-gray-12 leading-[160%] tracking-0 text-left'>
                {step.description}
              </p>

              {step.stepNumber === 1 && isAmazonSaleActive && (
                <p className='text-base font-semibold text-mi-on-surface-accent-secondary text-center leading-[25px] tracking-[-0.41px]'>
                  \ 期間限定！Amazonで購入すると10%OFF /
                </p>
              )}

              {step.buttons && <StepButtons buttons={step.buttons} />}
              {step.premiumFeatures && <PremiumFeaturesBox features={step.premiumFeatures} />}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
